import React from "react";

type TConversationHistoryBannerProps = {
	hasOlderTurns: boolean;
	status:
		| "idle"
		| "loading"
		| "success"
		| "error"
		| "empty";
	hiddenCount: number;
	nextLoadCount: number;
	lastLoadedCount: number;
	onLoadMore: () => void | Promise<void>;
	onJumpToLatest?: () => void;
};

const LOADER_DOTS = [0, 1, 2];

const formatEarlierMessageLabel = (
	count: number,
	options?: {
		loaded?: boolean;
	}
) => {
	const baseLabel =
		count === 1
			? "1 earlier message"
			: `${count} earlier messages`;

	if (options?.loaded) {
		return `Loaded ${baseLabel}.`;
	}

	return `${baseLabel} available above.`;
};

const ConversationHistoryBanner: React.FC<
	TConversationHistoryBannerProps
> = ({
	hasOlderTurns,
	status,
	hiddenCount,
	nextLoadCount,
	lastLoadedCount,
	onLoadMore,
	onJumpToLatest,
}) => {
	const isLoading = status === "loading";
	const isError = status === "error";
	const showJumpToLatestAction =
		!hasOlderTurns &&
		!isLoading &&
		!isError &&
		Boolean(onJumpToLatest);
	const showAction =
		(hasOlderTurns && (status === "idle" || isError)) ||
		showJumpToLatestAction;
	const buttonLabel = isError
		? "Try again"
		: nextLoadCount > 0
			? `Load ${nextLoadCount} more messages`
			: "Load more";
	const actionLabel = showJumpToLatestAction
		? "Jump to latest"
		: buttonLabel;
	const actionIconPath = showJumpToLatestAction
		? "M7 3.5V10.5M7 10.5L3.5 7M7 10.5L10.5 7"
		: "M7 10.5V3.5M7 3.5L3.5 7M7 3.5L10.5 7";
	const variant = isLoading
		? "loading"
		: isError
			? "error"
			: status === "success" && lastLoadedCount > 0
				? "success"
				: !hasOlderTurns || status === "empty"
					? "terminal"
					: "ready";

	let message =
		hiddenCount > 0
			? formatEarlierMessageLabel(hiddenCount)
			: "More messages are available above.";

	if (isLoading) {
		message =
			nextLoadCount > 0
				? `Loading ${nextLoadCount} more messages now.`
				: "Loading earlier messages now.";
	} else if (isError) {
		message =
			nextLoadCount > 0
				? `Couldn't load ${nextLoadCount} earlier messages.`
				: "Couldn't load earlier messages.";
	} else if (status === "success" && lastLoadedCount > 0) {
		message = `${formatEarlierMessageLabel(
			lastLoadedCount,
			{ loaded: true }
		)} Scroll up to view them.`;
	} else if (!hasOlderTurns) {
		message =
			"You are at the beginning of this conversation.";
	}

	return (
		<div
			className={`conversation-history-banner conversation-history-banner--${variant}`}
			role={isError ? "alert" : "status"}
			aria-live="polite"
		>
			<div className="conversation-history-banner__main">
				<div className="conversation-history-banner__status">
					<div
						className="conversation-history-banner__loader"
						aria-hidden="true"
					>
						{LOADER_DOTS.map((dot) => (
							<span
								key={dot}
								className="conversation-history-banner__loader-dot"
							/>
						))}
					</div>
					<p className="conversation-history-banner__message">
						{message}
					</p>
				</div>
				{showAction && (
					<button
						type="button"
						className="conversation-history-banner__button"
						onClick={() => {
							if (showJumpToLatestAction) {
								onJumpToLatest?.();
								return;
							}

							void onLoadMore();
						}}
					>
						<span>{actionLabel}</span>
						<span
							className="conversation-history-banner__button-icon"
							aria-hidden="true"
						>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d={actionIconPath}
									stroke="currentColor"
									strokeWidth="1.5"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</span>
					</button>
				)}
			</div>
		</div>
	);
};

export default ConversationHistoryBanner;
